import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BoxIcon, GiftIcon, PenToolIcon, FileTextIcon, BookOpenIcon, XIcon, UsersIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { SpiceLevelIndicator } from './SpiceLevelIndicator';
import { TagList } from './TagList';
import type { PastBox } from './types';
interface PastBoxesTabProps {
  relevantPastBoxes: PastBox[];
  expandedPastBox: number | null;
  setExpandedPastBox: React.Dispatch<React.SetStateAction<number | null>>;
}
export const PastBoxesTab: React.FC<PastBoxesTabProps> = ({
  relevantPastBoxes,
  expandedPastBox,
  setExpandedPastBox
}) => {
  const {
    darkMode
  } = useDarkMode();
  const selectedBox = relevantPastBoxes.find(b => b.id === expandedPastBox) ?? null;
  const closeModal = useCallback(() => setExpandedPastBox(null), [setExpandedPastBox]);
  // Escape key to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    if (selectedBox) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [selectedBox, closeModal]);
  if (relevantPastBoxes.length === 0) {
    return <div className={`p-12 rounded-2xl text-center border-2 border-dashed ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`} data-id="element-7562" data-mp="1uzrp4gfbdsqk">
        <BoxIcon className="h-12 w-12 mx-auto mb-3 opacity-40" data-id="element-7563" data-mp="1m1psed2m5m93" />
        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-7564" data-mp="1q1q9k5h3kbju">
          Past box information will be available soon!
        </p>
      </div>;
  }
  return <>
      {/* Card Grid: 1 col mobile, 3 col desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-7565" data-mp="3kezk80ldq33o">
        {relevantPastBoxes.map(pastBox => <button key={pastBox.id} onClick={() => setExpandedPastBox(pastBox.id)} className="group relative rounded-2xl overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 hover:-translate-y-0.5 transition-transform duration-200" data-id="element-7566" data-mp="x8d07ag1o5yv">
            <div className="aspect-[4/3] relative" data-id="element-7567" data-mp="j9vefi9p93jl">
              <img src={pastBox.image} alt={pastBox.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-7568" data-mp="1fyb4rgkmvgxr" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-7569" data-mp="174g8ky1hp0u9" />

              {/* Theme badge top-left */}
              <div className="absolute top-3 left-3" data-id="element-7570" data-mp="1zm24l52mxsum">
                <span className="bg-purple-600/90 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full" data-id="element-7571" data-mp="1znlgykl67jr4">
                  {pastBox.theme}
                </span>
              </div>

              {/* Count badges top-right */}
              <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end" data-id="element-7572" data-mp="275bagobxn9ex">
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white" data-id="element-7573" data-mp="2c4nyvdigcojm">
                  {pastBox.books.length}{' '}
                  {pastBox.books.length === 1 ? 'book' : 'books'}
                </span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white" data-id="element-7574" data-mp="2l4t5hlftniwq">
                  {pastBox.items.length} items
                </span>
              </div>

              {/* Name + description bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-4" data-id="element-7575" data-mp="2ztyzmuzsyixr">
                <p className="text-white font-bold text-sm leading-tight" data-id="element-7576" data-mp="2zw1q1g4vae30">
                  {pastBox.name}
                </p>
                <p className="text-white/60 text-xs mt-1 line-clamp-2" data-id="element-7577" data-mp="38unefrx0snbp">
                  {pastBox.description}
                </p>
              </div>
            </div>
          </button>)}
      </div>

      {/* Modal */}
      <AnimatePresence data-id="element-7578" data-mp="2r5jl5k6jvdtc">
        {selectedBox && <>
            {/* Backdrop */}
            <motion.div key="backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={closeModal} data-id="element-7579" data-mp="34wy8g37hq167" />

            {/* Panel — bottom sheet on mobile, centered dialog on desktop */}
            <motion.div key="panel" initial={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: [0.32, 0.72, 0, 1]
        }} className={`fixed z-50 w-full sm:w-auto sm:max-w-2xl
                bottom-0 left-0 right-0
                sm:inset-0 sm:m-auto sm:h-fit
                rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl
                max-h-[90vh] flex flex-col
                ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`} data-id="element-7580" data-mp="2hai0b0jb4xwk">
              {/* Modal Header */}
              <div className={`flex items-start justify-between px-6 py-4 border-b flex-shrink-0 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`} data-id="element-7581" data-mp="1tck1anh20639">
                <div data-id="element-7582" data-mp="2lteaa82o48oy">
                  <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7583" data-mp="2ltwatgwd592o">
                    {selectedBox.name}
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7584" data-mp="2qq0g07hz53vy">
                    {selectedBox.theme}
                  </p>
                </div>
                <button onClick={closeModal} className={`p-2 rounded-full transition-colors flex-shrink-0 ml-4 ${darkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} data-id="element-7585" data-mp="2grawkaafjbdl">
                  <XIcon className="w-5 h-5" data-id="element-7586" data-mp="231r01m7fzacq" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="overflow-y-auto flex-1 p-6 space-y-6" data-id="element-7587" data-mp="1y3fq7bh5czui">
                {/* Image */}
                <img src={selectedBox.image} alt={selectedBox.name} className="w-full rounded-xl object-cover aspect-video" data-id="element-7588" data-mp="1t1qavjmw600m" />

                {/* Description */}
                <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7589" data-mp="8kh90d909sp3">
                  {selectedBox.description}
                </p>

                {/* Books */}
                <div data-id="element-7590" data-mp="iiwzce07kxxj">
                  <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7591" data-mp="ii510vf88m3k">
                    <BookOpenIcon className="w-4 h-4" data-id="element-7592" data-mp="3m05ayg4cf1jt" />
                    Books Included
                  </h4>
                  <div className="space-y-4" data-id="element-7593" data-mp="3qx5iywkr6edk">
                    {selectedBox.books.map((book, i) => <div key={i} className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`} data-id="element-7594" data-mp="3vr9rvldkwwha" data-mpi={i}>
                        <h5 className={`font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7595" data-mp="1wljxu4q6dc20">
                          {book.title}
                        </h5>
                        <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7596" data-mp="25mrl7rr5kshb">
                          by{' '}
                          <Link to={`/author/${book.authorId}`} className={`font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7597" data-mp="2fk61nrd2th9m">
                            {book.author}
                          </Link>
                        </p>
                        {book.description && <p className={`text-sm mb-3 leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7598" data-mp="2yax8al5oitkb">
                            {book.description}
                          </p>}
                        <div className="mb-3" data-id="element-7599" data-mp="3cvjd3pnu2pfn">
                          <SpiceLevelIndicator level={book.spiceLevel} data-id="element-7600" data-mp="3hwz1nrp7ze2s" />
                        </div>
                        {book.tropes && book.tropes.length > 0 && <div className="mb-2" data-id="element-7601" data-mp="37yjbbqy0o8uc">
                            <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7602" data-mp="37wydnsgeo9s9">
                              Tropes
                            </span>
                            <TagList items={book.tropes} type="trope" data-id="element-7603" data-mp="2k9qb3iad3etx" />
                          </div>}
                        {book.contentWarnings && book.contentWarnings.length > 0 && <div className="mb-3" data-id="element-7604" data-mp="2bada7e0azykt">
                              <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7605" data-mp="2fa3x36mpluk2">
                                Content Warnings
                              </span>
                              <TagList items={book.contentWarnings} type="warning" data-id="element-7606" data-mp="3it9102mtx48r" />
                            </div>}
                        <div className={`pt-3 mt-1 border-t grid grid-cols-1 sm:grid-cols-2 gap-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7607" data-mp="eesnzr84bjvg">
                          <div className="flex items-center gap-1.5" data-id="element-7608" data-mp="5lp6n7ucqmvj">
                            <PenToolIcon className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7609" data-mp="9mhhg5ivgztx" />
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7610" data-mp="1243hcnn52bzk">
                              Cover:{' '}
                            </span>
                            <Link to={`/talent/designers/${book.coverDesignerId}`} className={`text-xs font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7611" data-mp="2huw3u4dn8nm3">
                              {book.coverDesigner}
                            </Link>
                          </div>
                          <div className="flex items-center gap-1.5" data-id="element-7612" data-mp="1yy4kxw20kaik">
                            <FileTextIcon className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7613" data-mp="2duluqiuk1h30" />
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7614" data-mp="3p7y2h4tfqjug">
                              Format:{' '}
                            </span>
                            <Link to={`/services/production/${book.formatterId}`} className={`text-xs font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7615" data-mp="3rcl19zth40dm">
                              {book.formatter}
                            </Link>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Items Included */}
                <div data-id="element-7616" data-mp="3ijzi4ilxjro9">
                  <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7617" data-mp="3ijr4gohzgic0">
                    <GiftIcon className="w-4 h-4" data-id="element-7618" data-mp="2pusifgaqow1f" />
                    Items Included
                  </h4>
                  <ul className="space-y-2" data-id="element-7619" data-mp="2utnbdb2f80z9">
                    {selectedBox.items.map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-7620" data-mp="1bzai882ub0o5" data-mpi={i}>
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" data-id="element-7621" data-mp="17xyvqbf9ivk6" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7622" data-mp="12ycl89qtjtf2">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {/* Featured Artists */}
                {selectedBox.artists && selectedBox.artists.length > 0 && <div data-id="element-7623" data-mp="k95xu9f2ydmx">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7624" data-mp="k6a12oeyxv02">
                      <UsersIcon className="w-4 h-4" data-id="element-7625" data-mp="acb0htujmja8" />
                      Featured Artists
                    </h4>
                    <div className="space-y-2" data-id="element-7626" data-mp="25dwo59472t5a">
                      {selectedBox.artists.map((artist, i) => <div key={i} data-id="element-7627" data-mp="2ad9c5x6sy69g" data-mpi={i}>
                          <Link to={`/talent/artists/${artist.id}`} className={`text-sm font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7628" data-mp="2ac6g7aumhdmo">
                            {artist.name}
                          </Link>
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7629" data-mp="1mphuee2ht10s">
                            {artist.items.join(', ')}
                          </p>
                        </div>)}
                    </div>
                  </div>}

                {/* Testimonial */}
                {selectedBox.testimonials && selectedBox.testimonials.length > 0 && <div className={`p-4 rounded-xl border-l-4 border-purple-500 ${darkMode ? 'bg-purple-900/20' : 'bg-purple-50'}`} data-id="element-7630" data-mp="10eq2af9qstoo">
                      <p className={`text-sm italic leading-relaxed mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7631" data-mp="vdk8ee4cj0dk">
                        "{selectedBox.testimonials[0].comment}"
                      </p>
                      <p className={`text-sm font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-700'}`} data-id="element-7632" data-mp="1j3wm65m9vrc2">
                        — {selectedBox.testimonials[0].name}
                      </p>
                    </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
};